import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import { BASE_URL } from '../constants/env'
import { IError } from 'types/error'
import { interceptorError } from './axiosInterceptorError'
import { getCsrfCookieToken } from './csrf'

/**
 * Headers type
 * @notExported
 */
type Headers = Record<string, string | undefined>

/**
 * Default headers
 * @notExported
 */
interface DefaultHeaders extends Headers {
  'Accept-Language': string
  'Content-Type': string
  Accept: string
}

export abstract class SubAPI {
  api: API

  constructor() {
    this.api = api
  }
}

/**
 * API class for initialising axios and handlers for requests and responses.
 * @notExported
 * @class
 * @name API
 * @property {AxiosInstance}
 * @notExported
 */
class API {
  // Axios provider
  private apiProvider: AxiosInstance
  // backend base URL
  private baseUrl: string
  constructor() {
    this.baseUrl = BASE_URL
    this.apiProvider = axios.create({
      baseURL: this.baseUrl,
      timeout: 300000,
      withCredentials: true,
    })

    // interceptor for handling automatic token refreshing and error situations with azure token
    this.apiProvider.interceptors.response.use(
      (response: AxiosResponse) => {
        return response
      },
      async error => {
        return interceptorError(error, this.apiProvider)
      }
    )
  }

  /**
   * Asserts the response from the API. If the response is not valid, it throws an error.
   *
   * @param request - Request data
   * @returns Axios response
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private static assert(request: AxiosResponse<any>) {
    try {
      const res = request
      return res.data
    } catch (error) {
      if (
        axios.isAxiosError(error) &&
        error.code !== 'ERR_CANCELED' &&
        error.name !== 'AbortError' &&
        error.name !== 'CanceledError'
      ) {
        throw API.errorHandler(error)
      }
    }
  }

  /**
   * Handles errors from the API.
   *
   * @param error - Returned Axios error.
   * @returns Formatted error object.
   */
  private static errorHandler(error: AxiosError): IError | undefined {
    if (error.code === 'ERR_CANCELED' || error.name === 'AbortError' || error.name === 'CanceledError') {
      return
    }
    if (error && error.response) {
      const data = error.response.data as {
        status: number
        parentError: Error | string
        key: string
        id: string
      }

      return {
        status: data.status,
        error: data.parentError,
        key: data.key ?? '',
        id: data.id,
      }
    }
    return {
      status: 500,
      error: new Error('Error requesting data!'),
      key: 'error.generic',
      id: '',
    }
  }

  /**
   * Creates default headers for the API request.
   *
   * @returns Default headers for cookie-authenticated requests.
   */
  private static async getDefaultHeaders(): Promise<DefaultHeaders> {
    const headers: DefaultHeaders = {
      'Accept-Language': 'en_US',
      'Content-Type': 'application/json',
      Accept: 'application/json',
    }

    const csrfToken = getCsrfCookieToken()
    if (csrfToken) {
      headers['x-csrf-token'] = csrfToken
    }

    return headers
  }

  /**
   * Creates headers for the API request.
   *
   * @param headers - Custom headers for the request.
   * @returns Request headers.
   */
  private static async getHeaders(headers: Headers | Record<string, string> | undefined = {}): Promise<Headers> {
    const defaultHeaders = await API.getDefaultHeaders()
    return { ...defaultHeaders, ...headers }
  }

  /**
   * Post request to the API.
   *
   * @param url - URL to send the request to.
   * @param body - Body of the request.
   * @param headers - Headers for the request.
   * @param baseUrl - Base API URL.
   * @param otherOptions - Additional request options.
   * @returns Axios response.
   */
  public async post<RequestBody, ResponseBody>(
    url: string,
    body: RequestBody,
    otherOptions?: AxiosRequestConfig,
    headers?: Headers,
    baseUrl: string = this.baseUrl
  ): Promise<ResponseBody> {
    const requestHeaders = await API.getHeaders(headers)

    return API.assert(
      await this.apiProvider.post(`${baseUrl}/${url}`, body, {
        headers: requestHeaders,
        ...otherOptions,
      })
    )
  }

  /**
   * Get request to the API.
   *
   * @param url - URL to send the request to.
   * @param params - Parameters of the request.
   * @param headers - Headers for the request.
   * @param otherOptions - Additional request options.
   * @returns Axios response.
   */
  public async get<ResponseBody, Params = undefined>(
    url: string,
    otherOptions?: AxiosRequestConfig,
    params?: Params,
    headers?: Headers
  ): Promise<ResponseBody> {
    const requestHeaders = await API.getHeaders(headers)
    return API.assert(
      await this.apiProvider.get(url, {
        params,
        headers: requestHeaders,
        ...otherOptions,
      })
    )
  }

  /**
   * Delete request to the API.
   *
   * @param url - URL to send the request to.
   * @param headers - Headers for the request.
   * @param otherOptions - Additional request options.
   * @returns Axios response.
   */
  public async delete<ResponseBody>(
    url: string,
    otherOptions?: AxiosRequestConfig,
    headers?: Headers
  ): Promise<ResponseBody> {
    const requestHeaders = await API.getHeaders(headers)
    return API.assert(
      await this.apiProvider.delete(url, {
        headers: requestHeaders,
        ...otherOptions,
      })
    )
  }

  /**
   * Put request to the API.
   *
   * @param url - URL to send the request to.
   * @param body - Body of the request.
   * @param headers - Headers of the request.
   * @param otherOptions - Additional request options.
   * @returns Axios response.
   */
  public async put<RequestBody, ResponseBody>(
    url: string,
    body: RequestBody,
    otherOptions?: AxiosRequestConfig,
    headers?: Headers
  ): Promise<ResponseBody> {
    const requestHeaders = await API.getHeaders(headers)
    return API.assert(
      await this.apiProvider.put(url, body, {
        headers: requestHeaders,
        ...otherOptions,
      })
    )
  }
}

const api = new API()
